import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VoiceAssistantTextarea } from '@/components/ui/VoiceAssistantTextarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Building2, ClipboardList, User, X, ExternalLink } from 'lucide-react';
import { useLocation } from 'wouter';
import { allowNextNavigationAfterConfirmedClose, useUnsavedNavigationRegistration } from '@/hooks/use-unsaved-changes-guard';
import { useToast } from '@/hooks/use-toast';
import { showInvalidFormFeedback } from '@/lib/invalidFormFeedback';
import { apiRequest } from '@/lib/queryClient';
import { HelpBubble } from '@/components/help/HelpBubble';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';

interface LeadModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isOpen?: boolean;
  onClose?: () => void;
  initialData?: any;
}

const leadSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  company: z.string().optional(),
  fullAddress: z.string().optional(),
  role: z.string().optional(),
  // Dati Azienda
  companyEmail: z.string().email("Email azienda non valida").optional().or(z.literal("")),
  companyPhone: z.string().optional(),
  website: z.string().optional(),
  // Dati Contatto
  contactMobile: z.string().optional(),
  contactOfficePhone: z.string().optional(),
  privateEmail: z.string().email("Email privata non valida").optional().or(z.literal("")),
  // Social Media
  linkedin: z.string().optional(),
  facebook: z.string().optional(),
  instagram: z.string().optional(),
  tiktok: z.string().optional(),
  source: z.array(z.string()).min(1, "Almeno una fonte è obbligatoria"),
  status: z.string().default("New"),
  notes: z.string().optional(),
});

type LeadFormData = z.infer<typeof leadSchema>;

const inputClass = "h-8 rounded-none text-xs";
const labelClass = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";
const sectionClass = "border border-border bg-card";
const sectionHeaderClass = "flex items-center justify-between gap-3 border-b border-border px-3 py-2";
const sectionTitleClass = "flex items-center gap-2 text-xs font-semibold text-foreground";
const errorClass = "text-xs font-medium text-destructive";

function buildLeadFormDefaults(initialData?: any): LeadFormData {
  return initialData ? {
    firstName: initialData.firstName || "",
    lastName: initialData.lastName || "",
    company: initialData.company || initialData.companyName || "",
    fullAddress: initialData.fullAddress || "",
    role: initialData.role || "",
    companyEmail: initialData.email || initialData.companyEmail || "",
    companyPhone: initialData.phone || initialData.companyPhone || "",
    website: initialData.website || "",
    contactMobile: initialData.mobilePhone || initialData.contactMobile || "",
    contactOfficePhone: initialData.officePhone || initialData.contactOfficePhone || "",
    privateEmail: initialData.privateEmail || "",
    linkedin: initialData.linkedin || "",
    facebook: initialData.facebook || "",
    instagram: initialData.instagram || "",
    tiktok: initialData.tiktok || "",
    source: Array.isArray(initialData.source) ? initialData.source : initialData.source ? [initialData.source] : [],
    status: initialData.status || "New",
    notes: initialData.notes || ""
  } : {
    firstName: "",
    lastName: "",
    company: "",
    fullAddress: "",
    role: "",
    companyEmail: "",
    companyPhone: "",
    website: "",
    contactMobile: "",
    contactOfficePhone: "",
    privateEmail: "",
    linkedin: "",
    facebook: "",
    instagram: "",
    tiktok: "",
    source: [],
    status: "New",
    notes: ""
  };
}

export default function LeadModal({
  open,
  onOpenChange,
  isOpen,
  onClose,
  initialData
}: LeadModalProps) {
  const [, navigate] = useLocation();
  const isModalOpen = open ?? isOpen ?? false;
  const isEditing = !!initialData?.id;
  const savedSuccessfullyRef = useRef(false);
  const handleModalClose = (newState: boolean): boolean => {
    if (!newState) {
      if (!savedSuccessfullyRef.current && isDirty && !window.confirm('Hai modifiche non salvate. Vuoi uscire senza salvarle?')) return false;
      savedSuccessfullyRef.current = false;
      allowNextNavigationAfterConfirmedClose();
      if (onOpenChange) onOpenChange(false);
      if (onClose) onClose();
    } else if (onOpenChange) {
      onOpenChange(true);
    }
    return true;
  };

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, setValue, watch, getValues, formState: { errors, isDirty } } = useForm<LeadFormData>({
    resolver: zodResolver(leadSchema),
    defaultValues: buildLeadFormDefaults(initialData)
  });
  useUnsavedNavigationRegistration(isModalOpen, isDirty);

  useEffect(() => {
    if (isModalOpen) {
      reset(buildLeadFormDefaults(initialData));
    }
  }, [initialData, isModalOpen, reset]);

  useEffect(() => {
    if (!isModalOpen || !isDirty) return;
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [isModalOpen, isDirty]);

  const createLead = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest('POST', '/api/leads', data);
      return response;
    },
    onSuccess: () => {
      toast({
        title: "Lead creato",
        description: "Il lead è stato creato con successo.",
      });
      // Force complete cache clear and immediate refetch
      queryClient.removeQueries();
      queryClient.clear();

      // Wait a moment and then refetch all data
      setTimeout(async () => {
        await queryClient.refetchQueries({ type: 'all' });
      }, 50);

      savedSuccessfullyRef.current = true;
      handleModalClose(false);
      reset();
    },
    onError: (error: any) => {
      toast({
        title: "Errore",
        description: error.message || "Errore durante la creazione del lead.",
        variant: "destructive",
      });
    },
  });

  const updateLead = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest('PATCH', `/api/leads/${initialData.id}`, data);
      return response;
    },
    onSuccess: () => {
      toast({
        title: "Lead aggiornato",
        description: "Il lead è stato aggiornato con successo.",
      });
      // Force complete cache clear and immediate refetch
      queryClient.removeQueries();
      queryClient.clear();

      // Wait a moment and then refetch all data
      setTimeout(async () => {
        await queryClient.refetchQueries({ type: 'all' });
      }, 50);

      savedSuccessfullyRef.current = true;
      handleModalClose(false);
    },
    onError: (error: any) => {
      toast({
        title: "Errore",
        description: error.message || "Errore durante l'aggiornamento del lead.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: LeadFormData) => {
    const submitData = {
      ...data,
      email: data.companyEmail,
      phone: data.companyPhone,
      mobilePhone: data.contactMobile,
      officePhone: data.contactOfficePhone,
      companyName: data.company,
    };

    if (initialData?.id) {
      updateLead.mutate(submitData);
    } else {
      createLead.mutate(submitData);
    }
  };

  const watchedSource = watch("source") || [];

  const addSource = (newSource: string) => {
    if (newSource && !watchedSource.includes(newSource)) {
      setValue("source", [...watchedSource, newSource], { shouldDirty: true });
    }
  };

  const removeSource = (sourceToRemove: string) => {
    setValue("source", watchedSource.filter(source => source !== sourceToRemove), { shouldDirty: true });
  };

  const availableSources = ["email", "telefono", "sito_web", "linkedin", "referral", "evento", "pubblicità", "altro"];

  return (
    <Dialog open={isModalOpen} onOpenChange={handleModalClose}>
      <DialogContent className="flex max-w-3xl flex-col gap-0 overflow-hidden rounded-none p-0 notion-dialog">
        <DialogHeader className="border-b border-border px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-sm font-semibold leading-5 text-foreground notion-title">
              {isEditing ? 'Modifica Lead' : 'Nuovo Lead'}
            </DialogTitle>
            {/* Stesso fix del DealModal (audit 21 lug): nessun modo di
                raggiungere la scheda completa del lead da questo pannello. */}
            {isEditing && initialData?.id && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0 gap-1.5 rounded-none text-xs"
                onClick={() => {
                  if (handleModalClose(false)) navigate(`/leads/${initialData.id}`);
                }}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Apri scheda completa
              </Button>
            )}
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit, showInvalidFormFeedback)} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
            <section className={sectionClass}>
              <div className={sectionHeaderClass}>
                <div>
                  <p className={sectionTitleClass}>
                    <User className="h-3.5 w-3.5" />
                    Identita'
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Persona, azienda e ruolo del lead.</p>
                </div>
              </div>
              <div className="grid gap-3 p-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="firstName" className={labelClass}>Nome</Label>
                  <Input
                    id="firstName"
                    placeholder="Nome"
                    className={inputClass}
                    {...register("firstName")}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="lastName" className={labelClass}>Cognome</Label>
                  <Input
                    id="lastName"
                    placeholder="Cognome"
                    className={inputClass}
                    {...register("lastName")}
                  />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="company" className={labelClass}>Azienda</Label>
                  <Input
                    id="company"
                    placeholder="Nome azienda"
                    className={inputClass}
                    {...register("company")}
                  />
                  {errors.company && (
                    <p className={errorClass}>{errors.company.message}</p>
                  )}
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="role" className={labelClass}>Ruolo</Label>
                  <Input
                    id="role"
                    placeholder="Ruolo del contatto"
                    className={inputClass}
                    {...register("role")}
                  />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <div className={sectionHeaderClass}>
                <p className={sectionTitleClass}>
                  <Building2 className="h-3.5 w-3.5" />
                  Dati azienda
                </p>
                <HelpBubble
                  context="lead-form"
                  entityType="lead"
                  entityData={getValues()}
                  position="right"
                />
              </div>
              <div className="grid gap-3 p-3 sm:grid-cols-3">
                <div className="space-y-1 sm:col-span-3">
                  <Label htmlFor="companyEmail" className={labelClass}>Email azienda</Label>
                  <Input
                    id="companyEmail"
                    type="email"
                    placeholder="info@azienda.it"
                    className={inputClass}
                    {...register("companyEmail")}
                  />
                  {errors.companyEmail && (
                    <p className={errorClass}>{errors.companyEmail.message}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="companyPhone" className={labelClass}>Telefono azienda</Label>
                  <Input
                    id="companyPhone"
                    placeholder="+39 02..."
                    className={inputClass}
                    {...register("companyPhone")}
                  />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="website" className={labelClass}>Sito web</Label>
                  <Input
                    id="website"
                    placeholder="https://www.azienda.it"
                    className={inputClass}
                    {...register("website")}
                  />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <div className={sectionHeaderClass}>
                <p className={sectionTitleClass}>
                  <User className="h-3.5 w-3.5" />
                  Dati persona
                </p>
                <HelpBubble
                  context="contact-form"
                  entityType="lead"
                  entityData={getValues()}
                  position="right"
                />
              </div>
              <div className="grid gap-3 p-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="contactMobile" className={labelClass}>Cellulare</Label>
                  <Input
                    id="contactMobile"
                    placeholder="+39 3XX..."
                    className={inputClass}
                    {...register("contactMobile")}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="contactOfficePhone" className={labelClass}>Telefono ufficio</Label>
                  <Input
                    id="contactOfficePhone"
                    placeholder="+39 02..."
                    className={inputClass}
                    {...register("contactOfficePhone")}
                  />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="privateEmail" className={labelClass}>Email personale</Label>
                  <Input
                    id="privateEmail"
                    type="email"
                    placeholder="nome@gmail.com"
                    className={inputClass}
                    {...register("privateEmail")}
                  />
                  {errors.privateEmail && (
                    <p className={errorClass}>{errors.privateEmail.message}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="linkedin" className={labelClass}>LinkedIn</Label>
                  <Input
                    id="linkedin"
                    placeholder="linkedin.com/in/..."
                    className={inputClass}
                    {...register("linkedin")}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="facebook" className={labelClass}>Facebook</Label>
                  <Input
                    id="facebook"
                    placeholder="facebook.com/..."
                    className={inputClass}
                    {...register("facebook")}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="instagram" className={labelClass}>Instagram</Label>
                  <Input
                    id="instagram"
                    placeholder="instagram.com/..."
                    className={inputClass}
                    {...register("instagram")}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="tiktok" className={labelClass}>TikTok</Label>
                  <Input
                    id="tiktok"
                    placeholder="tiktok.com/@..."
                    className={inputClass}
                    {...register("tiktok")}
                  />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <div className={sectionHeaderClass}>
                <p className={sectionTitleClass}>
                  <ClipboardList className="h-3.5 w-3.5" />
                  Stato e origine
                </p>
              </div>
              <div className="grid gap-3 p-3 sm:grid-cols-2">
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="fullAddress" className={labelClass}>Indirizzo</Label>
                  <AddressAutocomplete
                    id="fullAddress"
                    mode="address"
                    placeholder="Via, Citta', CAP"
                    className={inputClass}
                    value={watch('fullAddress') || ''}
                    onChange={(value) => setValue('fullAddress', value, { shouldDirty: true })}
                  />
                </div>
                <div className="space-y-2 sm:col-span-2" data-form-field="source">
                  <Label className={labelClass}>Fonte *</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {availableSources.map((source) => {
                      const isActive = watchedSource.includes(source);
                      return (
                        <button
                          key={source}
                          type="button"
                          onClick={() => isActive ? removeSource(source) : addSource(source)}
                          className={`h-7 border px-2 text-xs font-medium uppercase tracking-wide ${
                            isActive
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border bg-background text-muted-foreground hover:bg-muted"
                          }`}
                        >
                          {source}
                        </button>
                      );
                    })}
                  </div>
                  {watchedSource.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {watchedSource.map((source) => (
                        <Badge key={source} variant="secondary" className="h-5 rounded-none px-1.5 text-xs font-normal">
                          {source}
                          <X
                            className="ml-1 h-3 w-3 cursor-pointer"
                            onClick={() => removeSource(source)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                  {errors.source && (
                    <p className={errorClass}>{errors.source.message}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="status" className={labelClass}>Stato</Label>
                  <Select onValueChange={(value) => setValue("status", value, { shouldDirty: true })} defaultValue={watch("status")}>
                    <SelectTrigger className={inputClass}>
                      <SelectValue placeholder="Seleziona stato..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">Nuovo</SelectItem>
                      <SelectItem value="Contacted">Contattato</SelectItem>
                      <SelectItem value="Qualified">Qualificato</SelectItem>
                      <SelectItem value="Converted">Convertito</SelectItem>
                      <SelectItem value="Lost">Perso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="notes" className={labelClass}>Note</Label>
                  <VoiceAssistantTextarea
                    id="notes"
                    placeholder="Note aggiuntive..."
                    className="min-h-[72px] rounded-none text-xs"
                    value={watch("notes") || ""}
                    onValueChange={(value) => setValue("notes", value, { shouldDirty: true })}
                    fieldLabel="Note Lead"
                    martinaContext={{ terminalType: 'entity', entityType: 'lead', entityId: initialData?.id ? String(initialData.id) : undefined, route: initialData?.id ? `/leads/${initialData.id}` : '/leads' }}
                  />
                </div>
              </div>
            </section>
          </div>

          <div className="z-10 flex shrink-0 justify-end gap-2 border-t border-border bg-background px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <Button type="button" variant="outline" className="h-8 rounded-none px-3 text-xs" onClick={() => handleModalClose(false)}>
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={createLead.isPending || updateLead.isPending}
              className="h-8 rounded-none px-3 text-xs"
            >
              {createLead.isPending || updateLead.isPending
                ? "Salvataggio..."
                : isEditing ? "Aggiorna Lead" : "Crea Lead"
              }
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
